import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

// ─────────────────────────────────────────────────────────────────────────────
// Button — typed variant/size primitive that maps directly to the Design 5
// button system.  All existing behaviour (onClick, disabled, type, aria-*)
// is forwarded via the HTML attrs spread.
// ─────────────────────────────────────────────────────────────────────────────

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-soft"
  | "success"
  | "xs-secondary"
  | "tertiary";

export type ButtonSize = "default" | "xs";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

const BASE =
  "inline-flex items-center gap-1.5 border font-semibold whitespace-nowrap transition-[background,border-color,box-shadow] " +
  "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20 " +
  // Mobile touch target (Apple HIG 44 px); desktop can be smaller
  "min-h-[44px] sm:min-h-[34px]";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-white border-transparent rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-[#4338ca] active:bg-[#3730a3]",
  secondary:
    "bg-surface text-text border-border rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-surface-2 hover:border-[#dcd9d2]",
  ghost:
    "bg-transparent text-muted border-transparent rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-surface-2 hover:text-text",
  danger:
    "bg-red-600 text-white border-red-700 rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-red-700 active:bg-red-800",
  "danger-soft":
    "bg-red-soft text-red-text border-[#fcc8c2] rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-[#fde3df]",
  // "Done" / "Reviewed" indicator — non-interactive when disabled. No hover
  // darken; keep cursor default + full opacity so it reads as a finished
  // state, not a greyed-out broken control (Slack/Linear/Gmail pattern).
  success:
    "bg-green-soft text-green-text border-[#bbf7d0] rounded-lg h-[34px] px-3 text-[12.5px] " +
    "hover:bg-green-soft disabled:opacity-100 disabled:cursor-default",
  // Used for "Mark renewed" / "Confirm estimate" — small in-row action
  "xs-secondary":
    "bg-surface text-text border-border rounded-[7px] h-[30px] px-2.5 text-[12px] " +
    "hover:bg-surface-2 hover:border-[#dcd9d2] " +
    // xs overrides the min-height from BASE so it fits neatly inside obligation rows
    "min-h-0 sm:min-h-0",
  // Subtle text/link style for demoted secondary actions (e.g. Amend date)
  tertiary:
    "bg-transparent text-muted border-transparent rounded-lg h-[34px] px-2 text-[12.5px] " +
    "hover:text-text hover:underline underline-offset-2",
};

// forwardRef so the Button can be used as a Radix `asChild` trigger child
// (e.g. <Dialog.Trigger asChild><Button/>). Without it, Radix's Slot cannot
// attach its ref and React logs "Function components cannot be given refs".
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size: _size,   // reserved for future explicit size overrides; variant already encodes size
    className,
    children,
    ...rest
  },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(BASE, VARIANT_CLASSES[variant], className)}
    >
      {children}
    </button>
  );
});
